import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { marked } from "marked";
import DOMPurify from "dompurify";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import CallbackModal from "../components/CallbackModal";
import { API, FALLBACK_CONTENT } from "../lib/api";

marked.use({ breaks: true, gfm: true });

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [site, setSite] = useState(FALLBACK_CONTENT.site);
  const [error, setError] = useState(null);
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/content`).then((r) => { if (active) setSite({ ...FALLBACK_CONTENT.site, ...(r.data.site || {}) }); }).catch(() => {});
    axios
      .get(`${API}/articles/${encodeURIComponent(slug)}`)
      .then((r) => { if (active) setArticle(r.data); })
      .catch(() => { if (active) setError("not_found"); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (article?.title) document.title = `${article.title} · Big Bag Films`;
    if (article?.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = article.meta_description;
    }
    return () => { document.title = "Big Bag Films · Production"; };
  }, [article]);

  const html = article?.body
    ? DOMPurify.sanitize(marked.parse(article.body))
    : "";

  return (
    <main className="bbf-grain bg-[#050505] text-white min-h-screen">
      <CustomCursor />
      <Navigation site={site} onDiscussClick={() => setCallbackOpen(true)} />

      <article data-testid="article-page" className="pt-32 pb-20 px-5 sm:px-8 lg:px-12 max-w-4xl mx-auto">
        <Link to="/" className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white bbf-link">
          ← На главную
        </Link>

        {error === "not_found" ? (
          <div className="mt-20 text-center">
            <p className="overline">404</p>
            <h1 className="font-display uppercase text-3xl sm:text-5xl tracking-tight mt-3">Статья не найдена</h1>
            <p className="mt-6 text-white/55">Возможно, ссылка устарела или статья ещё не опубликована.</p>
          </div>
        ) : !article ? (
          <p className="mt-20 text-white/40 font-mono-jb text-[11px] uppercase tracking-[0.22em]">Загружаем…</p>
        ) : (
          <>
            <header className="mt-10 mb-12">
              {article.subtitle && (
                <p data-testid="article-subtitle" className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-[#a78bfa]">
                  {article.subtitle}
                </p>
              )}
              <h1 data-testid="article-title" className="font-display uppercase text-3xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95]">
                {article.title}
              </h1>
              {article.cover_url && (
                <img
                  src={article.cover_url}
                  alt={article.title}
                  className="mt-10 w-full aspect-[16/9] object-cover border border-white/10"
                />
              )}
            </header>

            <div
              data-testid="article-body"
              className="bbf-article-body text-white/80 text-base sm:text-lg leading-relaxed"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </>
        )}
      </article>

      <Footer site={site} />
      {callbackOpen && <CallbackModal onClose={() => setCallbackOpen(false)} />}
    </main>
  );
}
